import { useContext, useMemo } from 'react';
import type { ScaleInput } from '@visx/scale';
import type { AxisScale } from '@visx/xychart';
import { DataContext } from '@visx/xychart';

import { DIM_OPACITY_FACTOR } from '../../../constants';
import type { ChartDataPoint, ChartDataPointValue } from '../../../types/ChartDataPoint';
import { getScaledValueFactory } from '../private/utils/getScaledValueFactory';

import styles from './Series.module.css';

interface VisibleSeriesMarkersProps {
  data: ChartDataPoint[];
  activeXValue: number | string | null;
  isolatedXValues: Set<ChartDataPointValue>;
  getXValue: (d: ChartDataPoint | undefined) => ChartDataPointValue;
  xAccessor: (d: ChartDataPoint | undefined) => ScaleInput<AxisScale>;
  yAccessor: (d: ChartDataPoint | undefined) => ScaleInput<AxisScale>;
  color: string;
  dimmed: boolean;
}

export function VisibleSeriesMarkers({
  data,
  activeXValue,
  isolatedXValues,
  getXValue,
  xAccessor,
  yAccessor,
  color,
  dimmed,
}: VisibleSeriesMarkersProps) {
  const { xScale, yScale } = useContext(DataContext);

  const markers = useMemo(() => {
    if (!xScale || !yScale || (activeXValue === null && isolatedXValues.size === 0)) {
      return [];
    }
    const getX = getScaledValueFactory(xScale, xAccessor);
    const getY = getScaledValueFactory(yScale, yAccessor);

    const result: Array<{ key: string; x: number; y: number }> = [];
    for (const datum of data) {
      const xValue = getXValue(datum);
      const isActive = activeXValue !== null && xValue === activeXValue;
      if (!isActive && !isolatedXValues.has(xValue)) {
        continue;
      }
      const x = getX(datum);
      const y = getY(datum);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        result.push({ key: String(xValue), x, y });
      }
    }
    return result;
  }, [xScale, yScale, data, activeXValue, isolatedXValues, getXValue, xAccessor, yAccessor]);

  if (markers.length === 0) {
    return null;
  }

  return (
    <g>
      {markers.map((marker) => (
        <circle
          key={marker.key}
          cx={marker.x}
          cy={marker.y}
          r={4}
          fill={color}
          stroke="var(--lp-color-bg-ui-primary)"
          strokeWidth={2}
          className={`${styles.marker} ${styles.markerActive} ${styles.markerEnter}`}
          style={{
            opacity: dimmed ? DIM_OPACITY_FACTOR : undefined,
            pointerEvents: 'auto',
            transformOrigin: `${marker.x}px ${marker.y}px`,
          }}
        />
      ))}
    </g>
  );
}
